/**
 * UserPasswordResetFormPage
 * [UC-041] Password reset form accessed via email reset link
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../core/api-client';

interface PasswordResetForm {
  newPassword: string;
  confirmPassword: string;
}

export function UserPasswordResetFormPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [form, setForm] = useState<PasswordResetForm>({
    newPassword: '',
    confirmPassword: ''
  });
  // No explicit pre-check against a /validate endpoint — POST /auth/reset-password
  // is server-authoritative on the token and returns 400 with a clear detail
  // on invalid/expired. One network round-trip instead of two, and the form
  // can't fall out of sync with backend validation logic.
  const noToken = !token;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleInputChange = (field: keyof PasswordResetForm, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errorMessage) setErrorMessage('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.newPassword || !form.confirmPassword) {
      setErrorMessage(t('userPasswordResetForm.validation.passwordsRequired', 'Both password fields are required'));
      return;
    }

    if (form.newPassword !== form.confirmPassword) {
      setErrorMessage(t('userPasswordResetForm.validation.passwordsMismatch', 'Passwords do not match'));
      return;
    }

    if (form.newPassword.length < 8) {
      setErrorMessage(t('userPasswordResetForm.validation.passwordTooShort', 'Password must be at least 8 characters long'));
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      await api.post<void>('/auth/reset-password', {
        token,
        newPassword: form.newPassword,
        confirmPassword: form.confirmPassword
      });

      setSuccessMessage(t('userPasswordResetForm.success.passwordUpdated', 'Password updated successfully'));
      // Navigate to login after a delay
      setTimeout(() => {
        navigate('/user-login');
      }, 2000);
    } catch (error: any) {
      console.error('Password reset failed:', error);
      const status = error.response?.status;
      switch (status) {
        case 400:
          const message = error.response?.data?.message || '';
          if (message.includes('match')) {
            setErrorMessage(t('userPasswordResetForm.errors.passwordsMismatch', 'Passwords do not match - please try again'));
          } else {
            setErrorMessage(t('userPasswordResetForm.errors.invalidToken', 'Invalid or expired reset token - please request a new reset'));
          }
          break;
        default:
          setErrorMessage(t('userPasswordResetForm.errors.resetFailed', 'Failed to reset password. Please try again.'));
          break;
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestNewReset = () => {
    navigate('/user-password-reset-request');
  };

  const handleBackToLogin = () => {
    navigate('/user-login');
  };

  // Only hard-block when the URL has no token at all. Invalid / expired
  // tokens surface via the POST error path below so the user sees the
  // exact reason after submitting.
  if (noToken) {
    return (
      <div className="min-h-screen bg-[var(--surface-ground)] flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-sm sm:max-w-md text-center">
          <div className="bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] p-6">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-red-100 mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-[var(--text-primary)] mb-2">
              {t('userPasswordResetForm.tokenError.title', 'Reset Link Invalid')}
            </h2>
            <p className="text-sm text-[var(--text-muted)] mb-6">
              {t('userPasswordResetForm.errors.noToken', 'Invalid reset link - no token provided')}
            </p>
            <div className="space-y-3">
              <button
                onClick={handleRequestNewReset}
                className="w-full px-4 py-2.5 bg-[var(--accent-primary)] text-white rounded-lg hover:bg-[var(--accent-secondary)] transition-colors text-sm font-medium"
              >
                {t('userPasswordResetForm.buttons.requestNewReset', 'Request New Reset')}
              </button>
              <button
                onClick={handleBackToLogin}
                className="w-full px-4 py-2.5 text-[var(--accent-primary)] hover:text-[var(--accent-secondary)] transition-colors text-sm"
              >
                {t('userPasswordResetForm.buttons.backToLogin', 'Back to Login')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--surface-ground)] flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-sm sm:max-w-md">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[var(--surface-raised)] mb-4">
            <svg className="w-6 h-6 text-[var(--accent-primary)]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
            </svg>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)]">
            {t('userPasswordResetForm.title', 'Set New Password')}
          </h1>
          <p className="mt-2 text-sm text-[var(--text-muted)]">
            {t('userPasswordResetForm.subtitle', 'Enter your new password below')}
          </p>
        </div>

        {/* Reset Form */}
        <div className="bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] p-4 sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* New Password Field */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
                {t('userPasswordResetForm.fields.newPassword', 'New Password')}
              </label>
              <input
                type="password"
                placeholder="••••••••"
                value={form.newPassword}
                onChange={(e) => handleInputChange('newPassword', e.target.value)}
                disabled={isSubmitting}
                className="w-full px-3 py-2.5 sm:py-2 bg-[var(--surface-raised)] border border-[var(--border-subtle)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            {/* Confirm Password Field */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
                {t('userPasswordResetForm.fields.confirmPassword', 'Confirm Password')}
              </label>
              <input
                type="password"
                placeholder="••••••••"
                value={form.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                disabled={isSubmitting}
                className="w-full px-3 py-2.5 sm:py-2 bg-[var(--surface-raised)] border border-[var(--border-subtle)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            {/* Error Message */}
            {errorMessage && (
              <div className="bg-red-600/15 border border-red-600/25 text-red-700 px-4 py-3 rounded-lg text-sm">
                {errorMessage}
              </div>
            )}

            {/* Success Message */}
            {successMessage && (
              <div className="bg-green-600/15 border border-green-600/25 text-green-700 px-4 py-3 rounded-lg text-sm">
                {successMessage}
                <p className="mt-1 text-xs text-green-600">
                  {t('userPasswordResetForm.success.redirecting', 'Redirecting to login...')}
                </p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || !form.newPassword || !form.confirmPassword || !!successMessage}
              className="w-full mt-6 px-4 py-2.5 bg-[var(--accent-primary)] text-white rounded-lg hover:bg-[var(--accent-secondary)] disabled:bg-[var(--surface-overlay)] disabled:text-[var(--text-muted)] disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              {isSubmitting ? t('userPasswordResetForm.buttons.updating', 'Updating Password...') : t('userPasswordResetForm.buttons.updatePassword', 'Update Password')}
            </button>
          </form>
        </div>

        {/* Back to Login Link */}
        <div className="mt-6 text-center">
          <button
            onClick={handleBackToLogin}
            className="text-sm text-[var(--accent-primary)] hover:text-[var(--accent-secondary)] transition-colors"
          >
            {t('userPasswordResetForm.navigation.backToLogin', 'Back to login')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UserPasswordResetFormPage;